import { lazy, Suspense, useEffect, useState } from "react";
import "./styles/App.css";

const HeroScene = lazy(() => import("./components/HeroScene"));

const featuredProjects = [
  {
    number: "01",
    title: "VIP",
    eyebrow: "Systems / Developer tooling",
    description:
      "A Git-inspired version control system built in Rust, with repository initialization, SHA-1 object hashing, zlib compression, and Git-style object inspection.",
    tags: ["Rust", "CLI", "Git internals", "Content-addressable storage"],
    link: "https://github.com/Xoann/vip",
    callout: "Building Git from first principles",
  },
  {
    number: "02",
    title: "Journey Log",
    eyebrow: "Product engineering / Data visualization",
    description:
      "A goal and activity tracker that turns imported logs into long-term progress views, including charts, streaks, and contribution-style heatmaps.",
    tags: ["Next.js", "TypeScript", "React", "Tailwind CSS"],
    link: "https://github.com/Xoann/Journey-Log",
    live: "https://journeylog.netlify.app/",
    callout: "A modern, typed React product",
  },
  {
    number: "03",
    title: "ChefMate",
    eyebrow: "Full-stack / Collaborative",
    description:
      "A recipe platform with account-based storage, authentication, image uploads, recipe forking, and Firestore-backed CRUD workflows.",
    tags: ["JavaScript", "Firebase", "Firestore", "Authentication"],
    link: "https://github.com/Xoann/TheRecipeBook",
    live: "https://chefmate.ca",
    callout: "Cloud-backed product workflows",
  },
  {
    number: "04",
    title: "AniRadar",
    eyebrow: "Frontend / API integration",
    description:
      "A responsive anime schedule and progress tracker powered by live AniList data, with reusable UI components and client-side state management.",
    tags: ["Svelte", "JavaScript", "REST API"],
    link: "https://github.com/Xoann/what2watch",
    live: "https://animeradar.netlify.app",
    callout: "A live API-driven product",
  },
];

const experience = [
  {
    role: "DevOps Engineer Co-op",
    company: "Cerebras Systems",
    period: "May 2025 — Aug 2026",
    summary:
      "Built internal developer infrastructure for high-volume CI systems used by hundreds of engineers.",
    highlights: [
      "Rebuilt a Next.js and MongoDB CI dashboard, cutting large test-train load times from minutes to seconds.",
      "Created live and historical pipeline insights across 40+ daily PR trains running 40k–60k tests.",
      "Contributed to a Python Jenkins scheduler supporting roughly 100 regression workloads on Kubernetes and AWS.",
    ],
  },
  {
    role: "Grants Team Intern",
    company: "Azrieli Foundation",
    period: "May 2024 — Jun 2024",
    summary:
      "Used Python and pandas to clean, organize, and summarize operational data for reporting and executive review.",
  },
];

const skills = [
  {
    label: "Languages",
    value: "TypeScript, JavaScript, Python, Rust, Java, C, C#, SQL",
  },
  {
    label: "Product",
    value: "React, Next.js, Svelte, Tailwind CSS, REST APIs, MongoDB, Firebase",
  },
  {
    label: "Infrastructure",
    value: "Docker, Kubernetes, AWS, Jenkins, CI/CD, Linux/UNIX, Git",
  },
];

function ArrowIcon() {
  return <span aria-hidden="true">↗</span>;
}

function ThemeIcon({ theme }) {
  return theme === "dark" ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
    </svg>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    const savedTheme = window.localStorage.getItem("portfolio-theme");
    if (savedTheme) return savedTheme;
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("portfolio-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="Liam Maguire, home">
          LM<span>.</span>
        </a>
        <nav className="main-nav" aria-label="Primary navigation">
          <a href="#work">Work</a>
          <a href="#experience">Experience</a>
          <a href="#about">About</a>
        </nav>
        <div className="header-actions">
          <button
            className="icon-button"
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          >
            <ThemeIcon theme={theme} />
          </button>
          <a className="compact-cta" href="#contact">
            Let&apos;s talk
          </a>
        </div>
      </header>

      <main id="main-content">
        <section className="hero section-wrap" id="top">
          <div className="hero-copy">
            <p className="eyebrow"><span /> Liam Maguire · Computer Science @ UofT</p>
            <h1>
              I build thoughtful software for
              <em> messy, real-world problems.</em>
            </h1>
            <p className="hero-intro">
              I&apos;m a computer science student and former DevOps engineer at
              Cerebras Systems. I enjoy working across developer infrastructure,
              full-stack products, and systems programming.
            </p>
            <div className="hero-actions">
              <a className="button primary-button" href="#work">
                Explore my work <span aria-hidden="true">↓</span>
              </a>
              <a
                className="button secondary-button"
                href="/liam-maguire-resume.pdf"
                target="_blank"
                rel="noreferrer"
              >
                View Resume <ArrowIcon />
              </a>
            </div>
            <div className="hero-context" aria-label="Location and availability">
              <span>Toronto, Canada</span>
              <span>Open to software engineering roles</span>
            </div>
          </div>
          <Suspense fallback={<div className="hero-visual" aria-hidden="true" />}>
            <HeroScene />
          </Suspense>
        </section>

        <section className="marquee" aria-label="Areas of focus">
          <div>
            <span>Developer tooling</span><i>✦</i>
            <span>Systems programming</span><i>✦</i>
            <span>Full-stack engineering</span><i>✦</i>
            <span>CI infrastructure</span><i>✦</i>
          </div>
        </section>

        <section className="section-wrap work-section" id="work">
          <div className="section-heading">
            <div>
              <p className="eyebrow"><span /> Selected work</p>
              <h2>Projects with depth.</h2>
            </div>
            <p>
              A mix of systems programming, developer tooling, and products
              built for real people.
            </p>
          </div>

          <div className="featured-projects">
            {featuredProjects.map((project) => (
              <article className="project-card" key={project.title}>
                <div className="project-index">{project.number}</div>
                <div className="project-body">
                  <p className="project-eyebrow">{project.eyebrow}</p>
                  <h3>{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  <ul className="tag-list" aria-label={`${project.title} technologies`}>
                    {project.tags.map((tag) => <li key={tag}>{tag}</li>)}
                  </ul>
                </div>
                <div className="project-meta">
                  <p>{project.callout}</p>
                  <div className="project-links">
                    <a href={project.link} target="_blank" rel="noreferrer">
                      Source <ArrowIcon />
                    </a>
                    {project.live && (
                      <a href={project.live} target="_blank" rel="noreferrer">
                        Live <ArrowIcon />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>

        </section>

        <section className="experience-section" id="experience">
          <div className="section-wrap">
            <div className="section-heading inverse-heading">
              <div>
                <p className="eyebrow"><span /> Experience</p>
                <h2>Engineering at scale.</h2>
              </div>
              <p>
                Production experience across CI platforms, backend services,
                data-heavy interfaces, and cloud infrastructure.
              </p>
            </div>

            <div className="timeline">
              {experience.map((item) => (
                <article className="timeline-item" key={item.company}>
                  <p className="timeline-period">{item.period}</p>
                  <div className="timeline-role">
                    <h3>{item.role}</h3>
                    <p>{item.company}</p>
                  </div>
                  <div className="timeline-copy">
                    <p>{item.summary}</p>
                    {item.highlights && (
                      <ul>
                        {item.highlights.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-wrap about-section" id="about">
          <div className="about-copy">
            <p className="eyebrow"><span /> About</p>
            <h2>Curious across the stack. Serious about the details.</h2>
            <p>
              I&apos;m studying computer science at the University of Toronto.
              I like projects that sit between layers: the infrastructure
              developers rely on, the APIs that move data, and the interfaces
              that make complex systems understandable.
            </p>
            <p>
              I&apos;m currently looking for software engineering opportunities
              where I can learn quickly, own meaningful problems, and build
              reliable tools at scale.
            </p>
          </div>
          <div className="skills-list">
            {skills.map((skill) => (
              <div className="skill-row" key={skill.label}>
                <span>{skill.label}</span>
                <p>{skill.value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div className="section-wrap contact-inner">
            <p className="eyebrow"><span /> Get in touch</p>
            <h2>Have an interesting problem?</h2>
            <p>
              I&apos;m always happy to talk about software engineering,
              infrastructure, and opportunities to build useful things.
            </p>
            <a className="contact-link" href="mailto:liammaguire@outlook.com">
              liammaguire@outlook.com <ArrowIcon />
            </a>
            <div className="social-links">
              <a href="https://github.com/Xoann" target="_blank" rel="noreferrer">GitHub <ArrowIcon /></a>
              <a href="https://www.linkedin.com/in/liam-maguire-3b033b201/" target="_blank" rel="noreferrer">LinkedIn <ArrowIcon /></a>
              <a href="/liam-maguire-resume.pdf" target="_blank" rel="noreferrer">Resume <ArrowIcon /></a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer section-wrap">
        <span>© 2026 Liam Maguire</span>
        <a href="#top">Back to top ↑</a>
      </footer>
    </div>
  );
}

export default App;
